import React, { useState, useEffect } from 'react';
import { itemsApi, categoriesApi, uploadApi, Item, Category, ColorVariation } from '../../services/api';
import OverflowTooltip from '../common/OverflowTooltip';
import { getFullProductTitle } from '../../utils/productText';
import './ItemManagement.css';

const ItemManagement: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState<Item | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [categorySearchQueries, setCategorySearchQueries] = useState<{ [key: string]: string }>({});
    const [uploadingImages, setUploadingImages] = useState({
        mainImage: false,
        subMainImage: false,
        colorVariation: false
    });
    const [uploadProgress, setUploadProgress] = useState({
        mainImage: 0,
        subMainImage: 0,
        colorVariation: 0
    });
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        size: '',
        price: 0,
        mrp: 0,
        stock: 0,
        mainImage: '',
        subMainImage: '',
        colorVariations: [] as ColorVariation[]
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [itemsResponse, categoriesResponse] = await Promise.all([
                itemsApi.getAll(),
                categoriesApi.getAll()
            ]);
            // Backend returns 'products' not 'items'
            setItems(itemsResponse.data.products || itemsResponse.data.items || []);
            setCategories(categoriesResponse.data.categories || []);
        } catch (err) {
            setError('Failed to fetch data');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (file: File, onProgress?: (progress: number) => void) => {
        console.log('Uploading file:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');

        console.log('Requesting presigned URL for:', file.name, file.type);
        const response = await uploadApi.getPresignedUrl(file.name, file.type);

        if (!response.data || !response.data.presignedUrl) {
            console.error('Invalid response from presigned URL:', response);
            throw new Error('Failed to get presigned URL');
        }

        console.log('Uploading to S3:', response.data.presignedUrl);

        // Upload to S3 with original quality
        const uploadResponse = await fetch(response.data.presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });

        console.log('S3 Upload Response Status:', uploadResponse.status, uploadResponse.statusText);

        if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error('S3 Upload Error:', errorText);
            throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        console.log('Upload successful, image URL:', response.data.imageUrl);
        return response.data.imageUrl;
    };

    const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setError(null);
            setUploadingImages(prev => ({ ...prev, mainImage: true }));
            setUploadProgress(prev => ({ ...prev, mainImage: 0 }));
            const imageUrl = await handleImageUpload(file, (progress) => {
                setUploadProgress(prev => ({ ...prev, mainImage: progress }));
            });
            setFormData(prev => ({ ...prev, mainImage: imageUrl }));
            setUploadProgress(prev => ({ ...prev, mainImage: 100 }));
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to upload main image';
            setError(errorMsg);
            console.error('Main image upload error:', err);
        } finally {
            setUploadingImages(prev => ({ ...prev, mainImage: false }));
            setTimeout(() => setUploadProgress(prev => ({ ...prev, mainImage: 0 })), 500);
        }
    };

    const handleSubMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setError(null);
            setUploadingImages(prev => ({ ...prev, subMainImage: true }));
            setUploadProgress(prev => ({ ...prev, subMainImage: 0 }));
            const imageUrl = await handleImageUpload(file, (progress) => {
                setUploadProgress(prev => ({ ...prev, subMainImage: progress }));
            });
            setFormData(prev => ({ ...prev, subMainImage: imageUrl }));
            setUploadProgress(prev => ({ ...prev, subMainImage: 100 }));
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to upload design image';
            setError(errorMsg);
            console.error('Sub-main image upload error:', err);
        } finally {
            setUploadingImages(prev => ({ ...prev, subMainImage: false }));
            setTimeout(() => setUploadProgress(prev => ({ ...prev, subMainImage: 0 })), 500);
        }
    };

    const [newColorVariation, setNewColorVariation] = useState({ color: '', imageFile: null as File | null });

    const handleColorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewColorVariation(prev => ({ ...prev, color: e.target.value }));
    };

    const handleColorImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setNewColorVariation(prev => ({ ...prev, imageFile: file }));
        }
    };

    const addColorVariation = async () => {
        if (!newColorVariation.color.trim()) {
            setError('Please enter a color name');
            return;
        }

        if (!newColorVariation.imageFile) {
            setError('Please select an image');
            return;
        }

        if (formData.colorVariations.length >= 10) {
            setError('Maximum 10 color variations allowed');
            return;
        }

        try {
            setError(null);
            setUploadingImages(prev => ({ ...prev, colorVariation: true }));
            setUploadProgress(prev => ({ ...prev, colorVariation: 0 }));
            const imageUrl = await handleImageUpload(newColorVariation.imageFile, (progress) => {
                setUploadProgress(prev => ({ ...prev, colorVariation: progress }));
            });

            setFormData(prev => ({
                ...prev,
                colorVariations: [...prev.colorVariations, { color: newColorVariation.color, image: imageUrl }]
            }));

            setNewColorVariation({ color: '', imageFile: null });
            setUploadProgress(prev => ({ ...prev, colorVariation: 100 }));
            // Reset the file input
            const fileInput = document.getElementById('colorVariationImage') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        } catch (err) {
            setError('Failed to upload color variation');
        } finally {
            setUploadingImages(prev => ({ ...prev, colorVariation: false }));
            setTimeout(() => setUploadProgress(prev => ({ ...prev, colorVariation: 0 })), 500);
        }
    };

    const removeColorVariation = (index: number) => {
        setFormData(prev => ({
            ...prev,
            colorVariations: prev.colorVariations.filter((_, i) => i !== index)
        }));
    };

    const removeMainImage = () => {
        setFormData(prev => ({ ...prev, mainImage: '' }));
    };

    const removeSubMainImage = () => {
        setFormData(prev => ({ ...prev, subMainImage: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.categoryId) {
            setError('Name and category are required');
            return;
        }

        // Validate MRP - it's now mandatory
        if (!formData.mrp || formData.mrp <= 0) {
            setError('MRP is required and must be greater than 0');
            return;
        }

        // Validate that MRP >= price
        if (formData.mrp < formData.price) {
            setError('MRP must be greater than or equal to the current price');
            return;
        }

        try {
            setError(null);
            // MRP is now mandatory, always include it
            const submitData: any = {
                ...formData,
                mrp: Number(formData.mrp), // Ensure it's a number
                price: Number(formData.price) // Ensure price is a number
            };

            if (editingItem) {
                await itemsApi.update(editingItem.id, submitData);
            } else {
                await itemsApi.create(submitData);
            }
            await fetchData();
            resetForm();
        } catch (err) {
            setError('Failed to save item');
            console.error('Error saving item:', err);
        }
    };

    const handleEdit = (item: Item) => {
        setEditingItem(item);
        // If item doesn't have MRP, set it to price or require user to enter it
        const mrpValue = item.mrp && item.mrp > 0 ? item.mrp : (item.price || 0);
        setFormData({
            name: item.name,
            description: item.description || '',
            categoryId: item.categoryId,
            size: item.size || '',
            price: item.price || 0,
            mrp: mrpValue, // Set MRP to existing value or default to price
            stock: item.stock !== undefined ? item.stock : 0,
            mainImage: item.mainImage || '',
            subMainImage: item.subMainImage || '',
            colorVariations: item.colorVariations || []
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this item?')) {
            return;
        }

        try {
            await itemsApi.delete(id);
            await fetchData();
        } catch (err) {
            setError('Failed to delete item');
            console.error('Error deleting item:', err);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            categoryId: '',
            size: '',
            price: 0,
            mrp: 0,
            stock: 0,
            mainImage: '',
            subMainImage: '',
            colorVariations: []
        });
        setNewColorVariation({ color: '', imageFile: null });
        setEditingItem(null);
        setShowForm(false);
    };

    const getCategoryName = (categoryId: string) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown Category';
    };

    const toggleCategoryExpand = (categoryId: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleCategorySearch = (categoryId: string, query: string) => {
        setCategorySearchQueries(prev => ({
            ...prev,
            [categoryId]: query
        }));
    };

    const filterCategoryItems = (categoryItems: Item[], categoryId: string) => {
        const categorySearch = categorySearchQueries[categoryId] || '';
        if (!categorySearch.trim()) {
            return categoryItems;
        }
        const query = categorySearch.toLowerCase().trim();
        return categoryItems.filter(item =>
            item.id.toLowerCase().includes(query) ||
            item.name.toLowerCase().includes(query)
        );
    };

    // Group items by category
    const groupItemsByCategory = () => {
        const grouped: { [key: string]: Item[] } = {};

        let filteredItems = items;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filteredItems = items.filter(item =>
                item.id.toLowerCase().includes(query) ||
                item.name.toLowerCase().includes(query)
            );
        }

        filteredItems.forEach(item => {
            const categoryId = item.categoryId || 'uncategorized';
            if (!grouped[categoryId]) {
                grouped[categoryId] = [];
            }
            grouped[categoryId].push(item);
        });

        return grouped;
    };

    const groupedItems = groupItemsByCategory();
    const ITEMS_PER_PAGE = 6;

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="item-management">
            <div className="section-header">
                <h2>Item Management</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(true)}
                >
                    Add New Item
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="form-modal">
                    <div className="form-container">
                        <h3>{editingItem ? 'Edit Item' : 'Add New Item'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Item Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                    placeholder="Enter item name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="categoryId">Category *</label>
                                <select
                                    id="categoryId"
                                    value={formData.categoryId}
                                    onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter item description"
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="size">Size</label>
                                <input
                                    type="text"
                                    id="size"
                                    value={formData.size}
                                    onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                                    placeholder="Enter size information"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="mrp">MRP (Maximum Retail Price) (₹) *</label>
                                <input
                                    type="number"
                                    id="mrp"
                                    value={formData.mrp || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const mrpValue = value === '' ? 0 : parseFloat(value) || 0;
                                        setFormData(prev => ({ ...prev, mrp: mrpValue }));
                                    }}
                                    placeholder="Enter MRP"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <small className="form-hint">Original price before discount (must be greater than or equal to current price)</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="price">Current Price (₹) *</label>
                                <input
                                    type="number"
                                    id="price"
                                    value={formData.price}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                    placeholder="Enter current selling price"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <small className="form-hint">Selling price (discount will be calculated automatically if MRP is provided)</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="stock">Stock Quantity *</label>
                                <input
                                    type="number"
                                    id="stock"
                                    value={formData.stock}
                                    onChange={(e) => setFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                                    placeholder="Enter stock quantity"
                                    min="0"
                                    step="1"
                                    required
                                />
                                <small className="form-hint">Enter 0 to mark as out of stock. Any positive number means in stock.</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="mainImage">1. Main Image - All Design *</label>
                                <input
                                    type="file"
                                    id="mainImage"
                                    accept="image/*"
                                    onChange={handleMainImageUpload}
                                    disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation}
                                />
                                {uploadingImages.mainImage && (
                                    <div>
                                        <p className="upload-status">Uploading main image...</p>
                                        <div className="upload-progress">
                                            <div className="upload-progress-bar" style={{ width: `${uploadProgress.mainImage}%` }}></div>
                                        </div>
                                        {uploadProgress.mainImage > 0 && uploadProgress.mainImage < 100 && (
                                            <p className="upload-progress-text">{uploadProgress.mainImage}%</p>
                                        )}
                                    </div>
                                )}
                                {formData.mainImage && !uploadingImages.mainImage && (
                                    <div className="image-preview-single">
                                        <div className="image-item">
                                            <img src={formData.mainImage} alt="Main Design" loading="lazy" decoding="async" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={removeMainImage}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="subMainImage">2. Sub-Main Image - Design Detail</label>
                                <input
                                    type="file"
                                    id="subMainImage"
                                    accept="image/*"
                                    onChange={handleSubMainImageUpload}
                                    disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation}
                                />
                                {uploadingImages.subMainImage && (
                                    <div>
                                        <p className="upload-status">Uploading design detail image...</p>
                                        <div className="upload-progress">
                                            <div className="upload-progress-bar" style={{ width: `${uploadProgress.subMainImage}%` }}></div>
                                        </div>
                                        {uploadProgress.subMainImage > 0 && uploadProgress.subMainImage < 100 && (
                                            <p className="upload-progress-text">{uploadProgress.subMainImage}%</p>
                                        )}
                                    </div>
                                )}
                                {formData.subMainImage && !uploadingImages.subMainImage && (
                                    <div className="image-preview-single">
                                        <div className="image-item">
                                            <img src={formData.subMainImage} alt="Design Detail" loading="lazy" decoding="async" />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={removeSubMainImage}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>3. Color Variations (Max 10)</label>

                                {/* Add new color variation form */}
                                <div className="color-variation-add-form">
                                    <input
                                        type="text"
                                        placeholder="Enter color name (e.g., Red, Blue, Green)"
                                        value={newColorVariation.color}
                                        onChange={handleColorNameChange}
                                        disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation || formData.colorVariations.length >= 10}
                                        className="color-name-input"
                                    />
                                    <input
                                        type="file"
                                        id="colorVariationImage"
                                        accept="image/*"
                                        onChange={handleColorImageChange}
                                        disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation || formData.colorVariations.length >= 10}
                                        className="color-image-input"
                                    />
                                    {uploadingImages.colorVariation && (
                                        <div>
                                            <p className="upload-status">Uploading color variation...</p>
                                            <div className="upload-progress">
                                                <div className="upload-progress-bar" style={{ width: `${uploadProgress.colorVariation}%` }}></div>
                                            </div>
                                            {uploadProgress.colorVariation > 0 && uploadProgress.colorVariation < 100 && (
                                                <p className="upload-progress-text">{uploadProgress.colorVariation}%</p>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        onClick={addColorVariation}
                                        disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation || formData.colorVariations.length >= 10 || !newColorVariation.color.trim() || !newColorVariation.imageFile}
                                    >
                                        Add Color
                                    </button>
                                </div>

                                {/* Display existing color variations */}
                                {formData.colorVariations.length > 0 && (
                                    <div className="color-variations-preview">
                                        {formData.colorVariations.map((variation, index) => (
                                            <div key={index} className="color-variation-item">
                                                <div className="color-variation-image">
                                                    <img src={variation.image} alt={variation.color} loading="lazy" decoding="async" />
                                                    <button
                                                        type="button"
                                                        className="remove-image"
                                                        onClick={() => removeColorVariation(index)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                <div className="color-variation-name">{variation.color}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={uploadingImages.mainImage || uploadingImages.subMainImage || uploadingImages.colorVariation}>
                                    {editingItem ? 'Update' : 'Create'} Item
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Search Bar */}
            <div className="admin-search-container">
                <input
                    type="text"
                    className="admin-search-input"
                    placeholder="Search by Item ID or Title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                    <button
                        className="admin-search-clear"
                        onClick={() => setSearchQuery('')}
                        title="Clear search"
                    >
                        ×
                    </button>
                )}
            </div>

            {Object.keys(groupedItems).length > 0 ? (
                <div className="items-by-category">
                    {Object.entries(groupedItems).map(([categoryId, categoryItems]) => {
                        const isExpanded = expandedCategories.has(categoryId);
                        const filteredItems = filterCategoryItems(categoryItems, categoryId);
                        const displayItems = isExpanded ? filteredItems : filteredItems.slice(0, ITEMS_PER_PAGE);
                        const hasMoreItems = filteredItems.length > ITEMS_PER_PAGE;
                        const categorySearch = categorySearchQueries[categoryId] || '';

                        return (
                            <div key={categoryId} className="category-section">
                                <div className="category-header">
                                    <h3 className="category-section-title">
                                        {getCategoryName(categoryId)} ({categoryItems.length} {categoryItems.length === 1 ? 'item' : 'items'})
                                    </h3>
                                    {isExpanded && (
                                        <div className="category-search-container">
                                            <input
                                                type="text"
                                                className="category-search-input"
                                                placeholder="Search items in this category..."
                                                value={categorySearch}
                                                onChange={(e) => handleCategorySearch(categoryId, e.target.value)}
                                            />
                                            {categorySearch && (
                                                <button
                                                    className="category-search-clear"
                                                    onClick={() => handleCategorySearch(categoryId, '')}
                                                    title="Clear search"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {hasMoreItems && !isExpanded && (
                                        <button
                                            className="btn-see-more"
                                            onClick={() => toggleCategoryExpand(categoryId)}
                                        >
                                            See More ({categoryItems.length - ITEMS_PER_PAGE} more)
                                        </button>
                                    )}
                                    {isExpanded && (
                                        <button
                                            className="btn-see-less"
                                            onClick={() => toggleCategoryExpand(categoryId)}
                                        >
                                            Show Less
                                        </button>
                                    )}
                                </div>
                                <div className={isExpanded ? "items-scroll-container" : "items-grid"}>
                                    {displayItems.map((item) => (
                                        <div key={item.id} className="admin-item-card">
                                            <div className="admin-item-images">
                                                {item.mainImage ? (
                                                    <img src={item.mainImage} alt={item.name} loading="lazy" decoding="async" />
                                                ) : item.images && item.images.length > 0 ? (
                                                    <img src={item.images[0]} alt={item.name} loading="lazy" decoding="async" />
                                                ) : (
                                                    <div className="admin-no-image">No Image</div>
                                                )}
                                            </div>
                                            <div className="admin-item-content">
                                                <div className="admin-item-id-display">ID: {item.id}</div>
                                                <OverflowTooltip
                                                    text={item.name}
                                                    tooltipText={getFullProductTitle(item)}
                                                    className="admin-item-name"
                                                    as="h3"
                                                />
                                                {item.description && (
                                                    <OverflowTooltip text={item.description} className="admin-item-description" as="p" />
                                                )}
                                                {item.size && (
                                                    <p className="admin-item-size">Size: {item.size}</p>
                                                )}
                                                <div className="admin-item-price-row">
                                                    {item.mrp && item.mrp > item.price && (
                                                        <span className="admin-item-mrp-display">₹{item.mrp}</span>
                                                    )}
                                                    {item.price > 0 && (
                                                        <span className="admin-item-price">₹{item.price}</span>
                                                    )}
                                                </div>
                                                <div className="admin-item-stock">
                                                    <span className={`admin-stock-badge ${(item.stock === 0 || item.stock === undefined) ? 'out-of-stock' : 'in-stock'}`}>
                                                        {(item.stock === 0 || item.stock === undefined) ? 'Out of Stock' : `In Stock (${item.stock})`}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="admin-item-actions">
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {isExpanded && filteredItems.length === 0 && (
                                        <div className="empty-category-search">
                                            <p>No items found matching "{categorySearch}"</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : items && Array.isArray(items) && items.length === 0 ? (
                <div className="empty-state">
                    <p>No items found. Create your first item to get started.</p>
                </div>
            ) : searchQuery ? (
                <div className="empty-state">
                    <p>No items found matching "{searchQuery}"</p>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No items found. Create your first item to get started.</p>
                </div>
            )}

        </div>
    );
};

export default ItemManagement;
